import { useEffect, useRef, useCallback } from 'react';
import { useCommandStore, type TimelineMode } from '../store/commandStore.js';

interface UseTimelinePlaybackOptions {
  onTick: (cursor: number, mode: TimelineMode) => void;
  onComplete?: () => void;
}

const SPEED_MULTIPLIERS: Record<number, number> = {
  1: 0.04,
  2: 0.08,
  3: 0.14,
  4: 0.22,
};

export function useTimelinePlayback({ onTick, onComplete }: UseTimelinePlaybackOptions) {
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const onTickRef = useRef(onTick);
  const onCompleteRef = useRef(onComplete);

  onTickRef.current = onTick;
  onCompleteRef.current = onComplete;

  const { timelinePlaying, timelineSpeed, timelineMode, timelineCursor, setTimelineCursor, setTimelinePlaying } = useCommandStore();

  const tick = useCallback((time: number) => {
    if (lastTimeRef.current === null) {
      lastTimeRef.current = time;
    }
    const delta = (time - lastTimeRef.current) / 1000;
    lastTimeRef.current = time;

    const speedMultiplier = SPEED_MULTIPLIERS[timelineSpeed] || SPEED_MULTIPLIERS[1];
    const increment = delta * speedMultiplier;

    const newCursor = timelineCursor + increment;

    if (newCursor >= 1) {
      setTimelineCursor(1);
      setTimelinePlaying(false);
      onCompleteRef.current?.();
      return;
    }

    setTimelineCursor(newCursor);
    onTickRef.current(newCursor, timelineMode);
    rafRef.current = requestAnimationFrame(tick);
  }, [timelineSpeed, timelineMode, timelineCursor, setTimelineCursor, setTimelinePlaying]);

  useEffect(() => {
    if (!timelinePlaying) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTimeRef.current = null;
      return;
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTimeRef.current = null;
    };
  }, [timelinePlaying, tick]);

  const seek = useCallback((cursor: number) => {
    setTimelineCursor(cursor);
    onTickRef.current(cursor, useCommandStore.getState().timelineMode);
  }, [setTimelineCursor]);

  const togglePlay = useCallback(() => {
    const state = useCommandStore.getState();
    if (state.timelineCursor >= 1 && !state.timelinePlaying) {
      setTimelineCursor(0);
    }
    setTimelinePlaying(!state.timelinePlaying);
  }, [setTimelinePlaying, setTimelineCursor]);

  return { seek, togglePlay };
}
